import { Box } from "@mui/material";
import {
  ScaleOutlined as GramsIcon,
  PercentOutlined as PercentIcon,
} from "@mui/icons-material";
import { useState } from "react";
import { v2 } from "../ui/v2Tokens";
import { macro } from "../ui/macroTokens";
import TabsRow from "../../../dashboard/coach/TabsRow";
import MacroSliderRow from "../calculator/MacroSliderRow";
import NutritionDonut from "../../../client/NutritionDonut";
import { Card, SURFACE, TextButton } from "../ui/primitives";
import { GRAM } from "../ui/format";
import {
  POINTS,
  energyFigure,
  energyFromInput,
  energyLabel,
  energyToInput,
  macroFigure,
  macroFromInput,
  macroToInput,
  macroUnit,
  pointsFromMacro,
} from "../ui/nutritionUnits";
import { useNutritionDisplayMode } from "../../../../contexts/NutritionDisplayContext";
import type { MacroSet } from "../model/nutritionV2Model";

const KCAL_PER_GRAM = { protein: 4, carbs: 4, fats: 9 } as const;

const f1 = (n: number) => Math.round(n * 10) / 10;

type Props = {
  targets: MacroSet;
  /** true when the meals live elsewhere (a PDF), so these numbers are only the summary */
  summaryOnly?: boolean;
  /** omit to render read-only (a detailed menu's targets come from its meals) */
  onChange?: (targets: MacroSet) => void;
  /** what the calculator suggested, so the trainer can get back to it */
  recommended?: MacroSet;
  /** share of the protein target that should come from protein-dominant foods */
  qualityProteinPct?: number;
  onQualityProteinChange?: (pct: number) => void;
};

type MacroKey = "protein" | "carbs" | "fats";
type FieldKey = MacroKey | "fiber";

const MACRO_EN: Record<MacroKey, string> = {
  protein: "Protein",
  carbs: "Carbohydrates",
  fats: "Fat",
};

/** Slider ceilings, roughly the most a daily target would ever be. */
const GRAM_SLIDER_MAX: Record<MacroKey, number> = { protein: 350, carbs: 600, fats: 200 };

const MACROS: Array<{ key: MacroKey; label: string }> = [
  { key: "protein", label: "חלבון" },
  { key: "carbs", label: "פחמימות" },
  { key: "fats", label: "שומנים" },
];

/** How the trainer types the macros in. Either way, what is STORED is grams. */
type EntryMode = "grams" | "percent";

const MODE_TABS = [
  { key: "grams", label: "לפי גרמים", Icon: GramsIcon },
  { key: "percent", label: "לפי אחוזים", Icon: PercentIcon },
];

const kcalFromMacros = (m: MacroSet) =>
  m.protein * KCAL_PER_GRAM.protein + m.carbs * KCAL_PER_GRAM.carbs + m.fats * KCAL_PER_GRAM.fats;

const numberInput = {
  width: "100%",
  boxSizing: "border-box" as const,
  padding: "2px 0",
  border: "none",
  borderBottom: `1.5px solid ${v2.line}`,
  background: "transparent",
  fontSize: "26px",
  fontWeight: 800,
  fontFamily: "inherit",
  color: v2.ink,
  outline: "none",
  "&:focus": { borderBottomColor: v2.brand },
};

/**
 * The daily numbers for a menu with no meals in the builder — macro goals, or the summary
 * that rides alongside a PDF.
 *
 * Deliberately plain: five numbers and nothing else. The only colour is the macro's own,
 * on the card's edge.
 *
 * The two tabs are inverses of each other, so one side is always derived and no figure on
 * screen can contradict another:
 *
 *   לפי גרמים  — grams are typed, the calorie target IS their sum and is not editable
 *   לפי אחוזים — the calorie target is typed, the shares are typed, the grams follow
 *
 * The values are EDITABLE because the calculator is a recommendation, not the truth: for
 * these menu types the trainer's numbers (or the PDF's) are what the trainee actually
 * gets, so they are stored on the menu and may diverge from the calculator's suggestion.
 */
const MacroGoalsView = ({
  targets,
  summaryOnly,
  onChange,
  recommended,
  qualityProteinPct = 0,
  onQualityProteinChange,
}: Props) => {
  // Some coaches think in grams, others in "40/30/30". The menu stores grams either way.
  const [mode, setMode] = useState<EntryMode>("grams");
  const editable = Boolean(onChange);
  const byPercent = editable && mode === "percent";
  // calories and grams, or points — what every figure here is SHOWN and typed in. Stored: kcal/grams.
  const units = useNutritionDisplayMode();
  const inPoints = units === POINTS;
  // what the calorie field shows while it is being typed in, so "18." keeps its point (points mode)
  const [caloriesDraft, setCaloriesDraft] = useState<string | null>(null);
  const energy = energyFigure(targets.calories, units);
  /** "% מהקלוריות" — the share is the same number either way; only the metric's name changes */
  const shareOfEnergy = `% מה${energyLabel(units)}`;

  const differsFromRecommendation =
    recommended &&
    (["calories", "protein", "carbs", "fats", "fiber"] as const).some(
      (key) => Math.round(recommended[key]) !== Math.round(targets[key])
    );

  /** A macro's share of the calorie target — what the percentages are typed against. */
  const percentOf = (key: MacroKey) =>
    targets.calories > 0
      ? Math.round(((targets[key] * KCAL_PER_GRAM[key]) / targets.calories) * 100)
      : 0;

  const percentSum = percentOf("protein") + percentOf("carbs") + percentOf("fats");

  /** grams typed → the calorie target follows, so the two can never disagree */
  const setGrams = (key: FieldKey, value: string) => {
    const next = { ...targets, [key]: Number(value) || 0 };
    onChange?.({ ...next, calories: kcalFromMacros(next) });
  };

  /** a share typed → grams follow from the calorie target, which stays put */
  const setPercent = (key: MacroKey, value: string) => {
    const pct = Number(value) || 0;
    onChange?.({
      ...targets,
      [key]: Math.round((targets.calories * (pct / 100)) / KCAL_PER_GRAM[key]),
    });
  };

  /** the calorie target typed → the shares hold and the grams are rescaled to them */
  const setCalories = (value: string) => {
    const calories = energyFromInput(Number(value) || 0, units);
    const shares = {
      protein: percentOf("protein"),
      carbs: percentOf("carbs"),
      fats: percentOf("fats"),
    };
    onChange?.({
      ...targets,
      calories,
      protein: Math.round((calories * (shares.protein / 100)) / KCAL_PER_GRAM.protein),
      carbs: Math.round((calories * (shares.carbs / 100)) / KCAL_PER_GRAM.carbs),
      fats: Math.round((calories * (shares.fats / 100)) / KCAL_PER_GRAM.fats),
    });
  };

  return (
    <Card data-testid="macro-goals-view" sx={{ padding: "20px 22px" }}>
      {/* title + the recommendation escape hatch on one line */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "10px",
          marginBottom: "14px",
        }}
      >
        <Box sx={{ fontSize: "15px", fontWeight: 800, color: v2.ink }}>
          {summaryOnly ? "תפריט מקרו — לצד הקובץ" : "תפריט מקרו"}
        </Box>
        {editable && recommended && differsFromRecommendation && (
          <TextButton
            onClick={() => onChange?.(recommended)}
            sx={{ padding: "4px 11px", fontSize: "11.5px" }}
          >
            חזור להמלצת המחשבון
          </TextButton>
        )}
      </Box>

      {editable && (
        <TabsRow value={mode} onChange={(key: string) => setMode(key as EntryMode)} tabs={MODE_TABS} />
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        {/* The day in one band: the calorie figure on one side, the split on the other.
            Giving the donut its own half is what keeps it from crowding the number. */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "16px",
            flexWrap: "wrap",
            padding: "14px 16px",
            borderRadius: "14px",
            border: `1px solid ${v2.line}`,
            background: SURFACE,
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: "8px", flexWrap: "wrap" }}>
              {byPercent ? (
                <Box
                  component="input"
                  type="number"
                  aria-label={`יעד ${energyLabel(units)} יומי`}
                  min={0}
                  step={inPoints ? 0.1 : 10}
                  value={
                    inPoints
                      ? caloriesDraft ?? energyToInput(targets.calories, units)
                      : Math.round(targets.calories)
                  }
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    if (inPoints) setCaloriesDraft(e.target.value);
                    setCalories(e.target.value);
                  }}
                  onBlur={() => setCaloriesDraft(null)}
                  sx={{ ...numberInput, width: "130px", fontSize: "34px" }}
                />
              ) : (
                <Box
                  data-testid="calories-total"
                  sx={{ fontSize: "34px", fontWeight: 800, color: v2.ink, lineHeight: 1 }}
                >
                  {energy.text}
                </Box>
              )}
              <Box sx={{ fontSize: "13px", fontWeight: 600, color: v2.ink2 }}>{energy.unit} ליום</Box>
            </Box>
            {editable && (
              <Box sx={{ fontSize: "11.5px", marginTop: "5px", color: v2.ink3 }}>
                {byPercent ? (
                  <Box
                    component="span"
                    data-testid="percent-sum"
                    sx={{ fontWeight: 700, color: percentSum === 100 ? v2.ink3 : v2.warn }}
                  >
                    סה״כ {percentSum}%
                  </Box>
                ) : (
                  "מחושב מהמקרו"
                )}
              </Box>
            )}
          </Box>

          {/* the same donut the client page uses, so the split reads the same everywhere.
              It is fed CALORIES per macro, not grams, so it matches the shares below. */}
          {percentSum > 0 && (
            <Box
              data-testid="macro-donut"
              aria-label={`חלוקה: חלבון ${percentOf("protein")}%, פחמימות ${percentOf(
                "carbs"
              )}%, שומנים ${percentOf("fats")}%`}
              sx={{ display: "flex", alignItems: "center", gap: "16px", flexShrink: 0 }}
            >
              <NutritionDonut
                // in points mode the slices are each macro's points, so the tooltip reads in the
                // unit the donut's centre does (the donut converts the centre itself, from kcal)
                carbs={inPoints ? pointsFromMacro("carbs", targets.carbs) : Math.round(targets.carbs * KCAL_PER_GRAM.carbs)}
                proteins={
                  inPoints ? pointsFromMacro("protein", targets.protein) : Math.round(targets.protein * KCAL_PER_GRAM.protein)
                }
                fats={inPoints ? pointsFromMacro("fats", targets.fats) : Math.round(targets.fats * KCAL_PER_GRAM.fats)}
                calories={Math.round(targets.calories)}
                size={150}
                sliceColors={[macro.carbs.text, macro.protein.text, macro.fats.text]}
                emptyBorderColor={v2.line}
              />
              <Box sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                {MACROS.map((field) => (
                  <Box
                    key={field.key}
                    sx={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "11.5px" }}
                  >
                    <Box
                      sx={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        background: macro[field.key].text,
                      }}
                    />
                    <Box sx={{ color: v2.ink2 }}>{field.label}</Box>
                    <Box sx={{ fontWeight: 700, color: v2.ink }}>{percentOf(field.key)}%</Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>

        {/* the same macro card the calculator uses, so both screens read alike */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
            gap: "10px",
            alignItems: "stretch",
          }}
        >
          {MACROS.map((field) => {
            const grams = f1(targets[field.key]);
            const pct = percentOf(field.key);
            const kcalOf = Math.round(targets[field.key] * KCAL_PER_GRAM[field.key]);
            // the macro's own figure — grams, or its points INSTEAD of them (the slider moves in it too)
            const amount = macroToInput(field.key, targets[field.key], units);
            const setAmount = (v: number) => setGrams(field.key, String(macroFromInput(field.key, v, units)));
            return (
              <Box key={field.key} data-testid={`goal-${field.key}`} sx={{ display: "flex" }}>
                <MacroSliderRow
                  labelEn={MACRO_EN[field.key]}
                  labelHe={field.label}
                  color={macro[field.key].text}
                  value={byPercent ? pct : inPoints ? amount : grams}
                  unit={byPercent ? shareOfEnergy : inPoints ? `${macroUnit(units)} / יום` : "גרם / יום"}
                  valueLabel={byPercent ? `אחוז ${field.label}` : `יעד ${field.label}`}
                  onValueChange={
                    editable
                      ? (v: number) =>
                          byPercent
                            ? setPercent(field.key, String(v))
                            : setAmount(v)
                      : undefined
                  }
                  stats={
                    inPoints
                      ? // a macro's points ARE its energy, so the grams and kcal cells become one
                        [
                          { label: "נקודות", value: amount ? macroFigure(field.key, targets[field.key], units).text : "—" },
                          { label: shareOfEnergy, value: pct ? `${pct}%` : "—" },
                        ]
                      : [
                          { label: "גרם", value: grams ? `${grams}` : "—" },
                          { label: "קק״ל", value: kcalOf ? kcalOf.toLocaleString("he-IL") : "—" },
                          { label: "% מהקלוריות", value: pct ? `${pct}%` : "—" },
                        ]
                  }
                  sliderValue={byPercent ? pct : inPoints ? amount : grams}
                  sliderLabel={`סליידר ${field.label}`}
                  min={0}
                  max={
                    byPercent
                      ? 70
                      : macroToInput(field.key, GRAM_SLIDER_MAX[field.key], units)
                  }
                  step={byPercent ? 1 : inPoints ? 0.1 : 5}
                  onSliderChange={(v: number) =>
                    byPercent ? setPercent(field.key, String(v)) : setAmount(v)
                  }
                  footer={
                    // quality protein is a property OF the protein target — a share of it,
                    // not a target of its own — so it lives inside the protein card
                    field.key === "protein" && onQualityProteinChange ? (
                      <Box sx={{ marginTop: "10px", paddingTop: "9px", borderTop: `1px dashed ${v2.line}` }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "baseline",
                            justifyContent: "space-between",
                            gap: "6px",
                          }}
                        >
                          <Box sx={{ fontSize: "12px", color: v2.ink2, fontWeight: 600 }}>מתוכו איכותי</Box>
                          <Box data-testid="quality-protein" sx={{ fontSize: "12px", color: v2.ink2 }}>
                            {(() => {
                              // quality protein IS protein, so it converts like protein
                              const quality = macroFigure(
                                "protein",
                                (targets.protein * qualityProteinPct) / 100,
                                units,
                                { decimals: 0 }
                              );
                              return (
                                <>
                                  <Box component="span" sx={{ fontWeight: 800, color: macro.protein.text }}>
                                    {quality.text}
                                  </Box>
                                  {` ${quality.unit} · ${qualityProteinPct}%`}
                                </>
                              );
                            })()}
                          </Box>
                        </Box>
                        <Box
                          component="input"
                          type="range"
                          aria-label="אחוז חלבון איכותי"
                          min={0}
                          max={100}
                          step={5}
                          value={qualityProteinPct}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            onQualityProteinChange(Number(e.target.value) || 0)
                          }
                          sx={{
                            width: "100%",
                            height: "3px",
                            marginTop: "7px",
                            borderRadius: "2px",
                            accentColor: macro.protein.text,
                            cursor: "pointer",
                          }}
                        />
                      </Box>
                    ) : null
                  }
                />
              </Box>
            );
          })}

        </Box>

        {/* fiber carries no calories here, so it is never part of the split — one line */}
        <Box
          data-testid="goal-fiber"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            flexWrap: "wrap",
            padding: "10px 14px",
            borderRadius: "12px",
            border: `1px solid ${v2.line}`,
            borderInlineStart: `3px solid ${macro.fiber.text}`,
          }}
        >
          <Box sx={{ fontSize: "12.5px", fontWeight: 600, color: v2.ink }}>סיבים תזונתיים</Box>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: "4px" }}>
            {editable ? (
              <Box
                component="input"
                type="number"
                aria-label="יעד סיבים"
                min={0}
                step={1}
                value={f1(targets.fiber)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGrams("fiber", e.target.value)}
                sx={{ ...numberInput, width: "62px", fontSize: "20px", color: macro.fiber.text }}
              />
            ) : (
              <Box sx={{ fontSize: "20px", fontWeight: 800, color: macro.fiber.text }}>
                {f1(targets.fiber)}
              </Box>
            )}
            <Box sx={{ fontSize: "11px", color: v2.ink3 }}>{GRAM} / יום</Box>
          </Box>
        </Box>

        {summaryOnly && (
          <Box sx={{ fontSize: "12px", color: v2.ink2 }}>
            הזן את הערכים שמופיעים בקובץ — הארוחות עצמן נמצאות בו.
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default MacroGoalsView;
